import { useEffect, useRef } from 'react';

const HEADER_OFFSET_PROPERTY = '--header-offset';
const HEADER_OFFSET_FALLBACK_PX = 82;

/**
 * Tracks the real height of the sticky header block (currently: Header +
 * EmailVerificationBanner) and exposes it as the `--header-offset` CSS
 * variable, so other elements can position themselves below it correctly.
 *
 * @returns {React.RefObject<HTMLElement>}
 */
export const useHeaderOffset = () => {
  const headerOffsetRef = useRef(null);

  useEffect(() => {
    const node = headerOffsetRef.current;

    if (!node) {
      return undefined;
    }

    const root = document.documentElement;

    const resizeObserver = new ResizeObserver(([entry]) => {
      const rawHeight = entry.borderBoxSize
        ? entry.borderBoxSize[0].blockSize
        : entry.contentRect.height;

      root.style.setProperty(HEADER_OFFSET_PROPERTY, `${Math.floor(rawHeight)}px`);
    });

    resizeObserver.observe(node);

    return () => {
      resizeObserver.disconnect();
      root.style.setProperty(HEADER_OFFSET_PROPERTY, `${HEADER_OFFSET_FALLBACK_PX}px`);
    };
  }, []);

  return headerOffsetRef;
};
